'use strict'

/** Sentinel for "None of these" in v-radio-group (maps to null in reviewedMatches). */
const REVIEW_NONE_VALUE = '__none__'

const relationLabelEs = (relation) => {
  switch (relation) {
    case 'direct':
      return 'Exact / strong match'
    case 'broader':
      return 'Broader match'
    case 'partial':
      return 'Partial match'
    default:
      return relation || ''
  }
}

const conceptTypeLabelEs = (conceptType) => {
  switch (conceptType) {
    case 'occupation':
      return 'Occupation'
    case 'skill':
      return 'Skill'
    case 'knowledge':
      return 'Knowledge'
    default:
      return 'Unknown type'
  }
}

const evidenceSourceKind = (row) => {
  if (row && row.source && row.source.type) return row.source.type
  const id = String((row && row.evidenceId) || '')
  if (id.startsWith('competence:')) return 'competence'
  if (id.startsWith('skill:')) return 'skill'
  if (id.startsWith('job:')) return 'job'
  return 'unknown'
}

/**
 * Bloque del formulario People (Skills / Competences / Job Experiences).
 * ESCO skill y knowledge (y language/other) → tarjeta Skills.
 * Solo queda en Competences lo del extractor sin tipo ESCO aún (fallback por source).
 */
const isIscoUri = (uri) => String(uri || '').includes('/esco/isco/')

const withoutIsco = (rows) => (rows || []).filter((row) => !isIscoUri(row && row.uri))

/** Quita grupos ISCO de las sugerencias de ocupación. El catálogo los sigue teniendo. */
const omitIscoOccupationSuggestions = (draft) => {
  if (!draft || !draft.esco) return draft
  const esco = draft.esco
  const needsReview = (esco.needsReview || []).map((row) => Object.assign({}, row, {
    options: withoutIsco(row.options)
  })).filter((row) => row.options && row.options.length)
  return Object.assign({}, draft, {
    esco: Object.assign({}, esco, {
      autoSelected: withoutIsco(esco.autoSelected),
      needsReview
    })
  })
}

const resolvedConceptType = (row) => {
  if (!row || typeof row !== 'object') return row || ''
  if (isIscoUri(row.uri)) return 'occupation'
  return row.conceptType || ''
}

const escoFormBucket = (row) => {
  if (isIscoUri(row && row.uri)) return 'job'
  const ct = row && row.conceptType
  switch (ct) {
    case 'occupation':
      return 'job'
    case 'skill':
    case 'knowledge':
    case 'language':
    case 'other':
      return 'skill'
    default:
      break
  }
  const options = (row && row.options) || []
  for (const opt of options) {
    const bucket = escoFormBucket({ conceptType: opt && opt.conceptType, uri: opt && opt.uri })
    if (bucket !== 'unknown') return bucket
  }
  const src = evidenceSourceKind(row)
  if (src === 'job') return 'job'
  if (src === 'skill' || src === 'competence') return src
  return 'unknown'
}

const evidenceSourceLabelEs = (kind) => {
  switch (kind) {
    case 'competence':
      return 'CV competence'
    case 'skill':
      return 'CV skill'
    case 'job':
      return 'CV job title'
    default:
      return 'CV evidence'
  }
}

const conceptTypeBadgeEs = (conceptType) => {
  switch (conceptType) {
    case 'occupation':
      return 'OCCUPATION'
    case 'skill':
      return 'SKILL'
    case 'knowledge':
      return 'KNOWLEDGE'
    default:
      return 'UNKNOWN'
  }
}

/** Badge lista del extractor CV (no confundir con conceptType ESCO). */
const extractorCategoryBadgeEs = (category) => {
  switch (category) {
    case 'competence':
      return 'CV·COMPETENCE'
    case 'skill':
      return 'CV·SKILL'
    case 'job':
      return 'CV·JOB'
    default:
      return 'CV·?'
  }
}

const validateDraftShape = (draft) => {
  if (!draft || typeof draft !== 'object') return false
  if (!Array.isArray(draft.jobs)) return false
  if (!Array.isArray(draft.education)) return false
  const esco = draft.esco
  if (!esco || typeof esco !== 'object') return false
  return (
    Array.isArray(esco.autoSelected) &&
    Array.isArray(esco.needsReview) &&
    Array.isArray(esco.noMatch)
  )
}

const initReviewState = (draft) => {
  const esco = draft.esco || {}
  const selectedJobIndexes = (draft.jobs || []).map((_, index) => index)
  const selectedEducationIndexes = (draft.education || []).map((_, index) => index)
  const selectedAuto = {}
  for (const row of esco.autoSelected || []) {
    if (row && row.evidenceId) selectedAuto[row.evidenceId] = true
  }
  const reviewedMatches = {}
  for (const row of esco.needsReview || []) {
    if (row && row.evidenceId) reviewedMatches[row.evidenceId] = undefined
  }
  return {
    selectedJobIndexes,
    selectedEducationIndexes,
    educationGaps: (draft.education || []).map((edu) => {
      const missing = educationMissingFields(edu)
      return {
        name: missing.name,
        university: missing.university,
        start: missing.start,
        end: missing.end
      }
    }),
    selectedAuto,
    reviewedMatches
  }
}

const selectedReviewUris = (pick) => {
  if (Array.isArray(pick)) return pick.filter((uri) => typeof uri === 'string' && uri)
  if (typeof pick === 'string' && pick && pick !== REVIEW_NONE_VALUE) return [pick]
  return []
}

const EDUCATION_LEVELS = [
  'Early childhood Education',
  'Primary education',
  'Lower secondary education',
  'Upper secondary education',
  'Post-secondary non-tertiary education',
  'Short-cycle tertiary education',
  "Bachelor's or equivalent",
  "Master's or equivalent",
  'Doctoral or equivalent',
  'Other'
]

const educationYear = (value) => {
  const match = String(value == null ? '' : value).match(/\b(?:19|20)\d{2}\b/)
  return match ? match[0] : ''
}

const educationMissingFields = (edu) => {
  const row = edu || {}
  return {
    name: !String(row.title || row.name || '').trim(),
    university: !String(row.university || '').trim(),
    level: EDUCATION_LEVELS.indexOf(String(row.level || '').trim()) < 0,
    start: !educationYear(row.start != null ? row.start : row.startYear),
    end: !educationYear(row.end != null ? row.end : row.endYear)
  }
}

const educationRowComplete = (edu) => {
  const missing = educationMissingFields(edu)
  return !missing.name && !missing.university && !missing.level && !missing.start && !missing.end
}

const selectedEducationComplete = (draft, review) => {
  const selected = new Set((review && review.selectedEducationIndexes) || [])
  const rows = (draft && draft.education) || []
  for (let index = 0; index < rows.length; index++) {
    if (!selected.has(index)) continue
    if (!educationRowComplete(rows[index])) return false
  }
  return true
}

const isReviewComplete = (draft, review) => {
  if (!draft || !review) return false
  const needs = (draft.esco && draft.esco.needsReview) || []
  for (const row of needs) {
    if (!row || !row.evidenceId) continue
    const pick = review.reviewedMatches[row.evidenceId]
    if (pick === undefined) return false
    if (Array.isArray(pick) && selectedReviewUris(pick).length === 0) return false
  }
  return selectedEducationComplete(draft, review)
}

const buildReviewedDraft = (draft, review) => {
  const selectedEd = new Set(review.selectedEducationIndexes || [])
  // Lista jobs del extractor: ignorada de momento; ocupaciones van vía ESCO (evidence job:*).
  const jobs = []
  const education = (draft.education || []).filter((_, index) => selectedEd.has(index))

  /** @type {Array<{uri, preferredLabel, conceptType, relation, evidenceId, evidence}>} */
  const esco = []

  for (const row of (draft.esco && draft.esco.autoSelected) || []) {
    if (!row || !row.evidenceId) continue
    if (review.selectedAuto[row.evidenceId] === false) continue
    if (isIscoUri(row.uri)) continue
    esco.push({
      uri: row.uri,
      preferredLabel: row.preferredLabel,
      conceptType: resolvedConceptType(row),
      relation: row.relation,
      evidenceId: row.evidenceId,
      evidence: row.evidence,
      source: row.source,
      extractorCategory: row.extractorCategory || (row.source && row.source.type)
    })
  }

  for (const row of (draft.esco && draft.esco.needsReview) || []) {
    if (!row || !row.evidenceId) continue
    const pick = review.reviewedMatches[row.evidenceId]
    if (pick === undefined || pick === null) continue
    const uris = selectedReviewUris(pick)
    for (const uri of uris) {
      const opt = (row.options || []).find((o) => o && o.uri === uri)
      if (!opt || isIscoUri(opt.uri)) continue
      esco.push({
        uri: opt.uri,
        preferredLabel: opt.preferredLabel,
        conceptType: resolvedConceptType(opt),
        relation: opt.relation,
        evidenceId: uris.length > 1 ? row.evidenceId + '::' + opt.uri : row.evidenceId,
        evidence: row.evidence,
        source: row.source,
        extractorCategory: row.extractorCategory || (row.source && row.source.type)
      })
    }
  }

  return { jobs, education, esco }
}

const reviewRadioValue = (reviewedMatches, evidenceId) => {
  const value = reviewedMatches[evidenceId]
  if (value === undefined) return null
  if (value === null) return REVIEW_NONE_VALUE
  return value
}

const setReviewRadio = (reviewedMatches, evidenceId, radioValue) => {
  const resolved = radioValue === REVIEW_NONE_VALUE ? null : radioValue
  return Object.assign({}, reviewedMatches, { [evidenceId]: resolved })
}

const isReviewOptionChecked = (reviewedMatches, evidenceId, uri) => {
  return selectedReviewUris(reviewedMatches && reviewedMatches[evidenceId]).indexOf(uri) >= 0
}

const isReviewNoneChecked = (reviewedMatches, evidenceId) => {
  return !!(reviewedMatches && reviewedMatches[evidenceId] === null)
}

/** Marcar una coincidencia quita "Ninguna de estas". Desmarcar la última deja el grupo sin decidir. */
const toggleReviewOption = (reviewedMatches, evidenceId, uri, checked) => {
  const current = selectedReviewUris(reviewedMatches && reviewedMatches[evidenceId])
  const next = checked
    ? (current.indexOf(uri) >= 0 ? current : current.concat([uri]))
    : current.filter((item) => item !== uri)
  return Object.assign({}, reviewedMatches, {
    [evidenceId]: next.length ? next : undefined
  })
}

/** "Ninguna de estas" descarta las coincidencias ya marcadas de ese grupo. */
const toggleReviewNone = (reviewedMatches, evidenceId, checked) => {
  return Object.assign({}, reviewedMatches, {
    [evidenceId]: checked ? null : undefined
  })
}

module.exports = {
  REVIEW_NONE_VALUE,
  EDUCATION_LEVELS,
  relationLabelEs,
  conceptTypeLabelEs,
  conceptTypeBadgeEs,
  isIscoUri,
  omitIscoOccupationSuggestions,
  resolvedConceptType,
  extractorCategoryBadgeEs,
  evidenceSourceKind,
  escoFormBucket,
  evidenceSourceLabelEs,
  validateDraftShape,
  initReviewState,
  isReviewComplete,
  educationMissingFields,
  educationRowComplete,
  buildReviewedDraft,
  reviewRadioValue,
  setReviewRadio,
  isReviewOptionChecked,
  isReviewNoneChecked,
  toggleReviewOption,
  toggleReviewNone
}
